import MuiAppBar from '@mui/material/AppBar';
import MuiDrawer from '@mui/material/Drawer';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { styled, Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import { Link } from 'react-router';

import type { CSSObject, ListItemButtonProps, AppBarProps as MuiAppBarProps } from '@mui/material';

interface IItemListProps {
  openDrawer: boolean;
  label: string;
  icon: React.ReactNode;
  url: string;
}

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const AppBar = (drawerWidth: number | string) =>
  styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
  })<AppBarProps>(({ theme, open }) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
      marginLeft: drawerWidth,
      width: `calc(100% - ${drawerWidth}px)`,
      transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    }),
  }));

const Drawer = (
  drawerWidth: number | string,
  openedMixin: (theme: Theme) => CSSObject,
  closedMixin: (theme: Theme) => CSSObject,
) =>
  styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  }));

const ItemList = (props: IItemListProps) => {
  const { openDrawer, label, icon, url } = props;

  return (
    <Link to={url}>
      <ListItem disablePadding sx={{ display: 'block' }}>
        <ListItemButton
          sx={{
            minHeight: 48,
            justifyContent: openDrawer ? 'initial' : 'center',
            px: 2.5,
          }}
        >
          <Tooltip title={label} placement="right">
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: openDrawer ? 3 : 'auto',
                justifyContent: 'center',
              }}
            >
              {icon}
            </ListItemIcon>
          </Tooltip>

          <ListItemText primary={label} sx={{ opacity: openDrawer ? 1 : 0 }} />
        </ListItemButton>
      </ListItem>
    </Link>
  );
};

const ListItemTextCustom = styled(ListItemText)(({ theme }) => ({
  color: theme.palette.grey[500],
  span: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: '1rem',
  },
}));

interface StyledListProps {
  isChildren?: boolean;
  isSelected?: boolean;
  isOpenSidebar?: boolean;
}

const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) =>
    prop !== 'isChildren' && prop !== 'isSelected' && prop !== 'isOpenSidebar',
})<StyledListProps>(({ theme, isChildren, isSelected, isOpenSidebar }) => ({
  paddingLeft: isChildren ? theme.spacing(isOpenSidebar ? 2 : 2.3) : theme.spacing(2),
  backgroundColor: isOpenSidebar ? theme.palette.grey[50] : 'transparent',
  borderRadius: isOpenSidebar ? '1rem' : 0,
  width: isOpenSidebar ? 'calc(100% - 2rem)' : '100%',
  margin: isOpenSidebar ? `${theme.spacing(0.5)} auto` : 0,
  overflow: 'hidden',
  gap: isOpenSidebar ? theme.spacing(2) : theme.spacing(3),
  ':hover': {
    backgroundColor: theme.palette.primary.lighter,
    span: {
      color: theme.palette.secondary.main,
    },
    svg: {
      color: theme.palette.primary.main,
    },
  },
  '&& span': {
    color: isSelected ? theme.palette.secondary.main : theme.palette.grey[500],
  },
  svg: {
    color: isSelected
      ? theme.palette.primary.main
      : isChildren
        ? theme.palette.grey[300]
        : theme.palette.grey[500],
    width: isChildren ? '1.25rem' : '1.5rem',
  },
}));

interface ListItemButtonChildrenProps extends ListItemButtonProps {
  isChildren?: boolean;
  to?: string;
  target?: string;
  isSelected?: boolean;
  isOpenSidebar?: boolean;
  href?: string;
  rel?: string;
}

const ListItemButtonChildren: React.FC<ListItemButtonChildrenProps> = (props) => {
  const { isSelected, isChildren, isOpenSidebar, ...rest } = props;
  return (
    <StyledListItemButton
      isSelected={isSelected}
      isChildren={isChildren}
      isOpenSidebar={isOpenSidebar}
      {...rest}
    />
  );
};

export { AppBar, Drawer, DrawerHeader, ItemList, ListItemButtonChildren, ListItemTextCustom };
